/**
 * Administration → Rights (1.8.0, admin; docs/dev/rights-1.8.0-build.md, plan sections 2, 4 and 6): "Use rights
 * management" and Add rights fields (published through the normal model path, withRightsFields) with the status of
 * the fields; the policy table (rights-policy-<state>; Withdrawn is always Block); the ending-soon days, the digest
 * hour and the time zone; and the counts, each a link to the Library's Rights filter.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Copyright, Loader2, Plus } from 'lucide-react';
import {
  RIGHTS_ROLE_LABELS, rightsRoleProblems, withRightsFields, type RightsSettings,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useAdminModel, useRightsCounts, useRightsSettings, useSaveRightsSettings } from '../../api/hooks';
import { queryKeys } from '../../api/queryKeys';
import { toast } from '../../components/Toasts';
import { TableScroll } from '../../components/TableScroll';
import { timeZones } from '../assignments/zoned';
import { RIGHTS_COUNT_KEYS, freshRightsFieldIds, policyRows, rightsStateCause, rightsCountLabel, rightsFieldStatus, rightsFilterHref, setPolicy } from './rightsPolicy';

const HOURS = Array.from({ length: 24 }, (_, h) => h);

export function RightsAdmin() {
  const qc = useQueryClient();
  const q = useRightsSettings();
  const save = useSaveRightsSettings();
  const model = useAdminModel();
  const [v, setV] = useState<RightsSettings | null>(null);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (q.data && !v) {
      const { updatedAt: _u, ...rest } = q.data;
      void _u;
      setV(rest);
    }
  }, [q.data, v]);
  const counts = useRightsCounts(RIGHTS_COUNT_KEYS, Boolean(q.data?.enabled));
  if (!v || !q.data) return <div className="page" data-testid="rights-admin"><h2>Rights</h2><div className="skeleton" style={{ height: 160 }} /></div>;

  const { updatedAt: _saved, ...saved } = q.data;
  void _saved;
  const dirty = JSON.stringify(v) !== JSON.stringify(saved);
  const def = model.data?.current.definition;
  const status = rightsFieldStatus(def);
  const missing = status.filter((s) => !s.field);
  const fieldProblems = def ? rightsRoleProblems(def) : [];

  async function submit(next: RightsSettings = v!) {
    try {
      const r = await save.mutateAsync(next);
      const { updatedAt: _x, ...rest } = r;
      void _x;
      setV(rest);
      // Summaries carry rights (or stop carrying them) from now on.
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.assets() }), qc.invalidateQueries({ queryKey: ['asset'] }), qc.invalidateQueries({ queryKey: queryKeys.rightsCounts() })]);
      toast('Rights settings saved');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  /** Add rights fields: a new model version with the Rights section and the missing roles' fields. */
  async function addFields() {
    if (!model.data) return;
    setAdding(true);
    try {
      const cur = model.data.current.definition;
      // Field ids are permanent: a template id used before (say the rights fields were removed once) gets a fresh one.
      const ids = await freshRightsFieldIds(cur, async (id) =>
        (await api<{ available: boolean }>(`/api/v1/metadata/field-ids/${encodeURIComponent(id)}/available`)).available);
      const next = withRightsFields(cur, ids);
      const res = await api<{ version: number }>('/api/v1/admin/metadata-schema/publish', {
        method: 'POST', json: { baseVersion: model.data.current.version, definition: next, acknowledgeArchiving: false },
      });
      await Promise.all([qc.invalidateQueries({ queryKey: queryKeys.adminModel() }), qc.invalidateQueries({ queryKey: queryKeys.schema() }), qc.invalidateQueries({ queryKey: queryKeys.assets() })]);
      toast(`Rights fields added · model version ${res.version}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="page rights-admin" data-testid="rights-admin">
      <h2>Rights</h2>
      <p className="lede">
        Keep each asset’s rights (a status, the holder, the dates it may be used and its restrictions) and check them when material leaves PRISM:
        exports, Send to edit, share links and workflow deliveries. Editors can always cut with any material. <Link to="/help/rights">About rights</Link>
      </p>

      <section className="panel-card" data-testid="rights-switch">
        <h3><Copyright size={16} color="var(--accent-strong)" /> Rights management</h3>
        <label className="toggle-row">
          <input type="checkbox" checked={v.enabled} onChange={(e) => setV({ ...v, enabled: e.target.checked })} data-testid="rights-enabled" />
          <span><b>Use rights management</b><br /><span className="faint" style={{ fontSize: 12 }}>Off: rights fields stay ordinary metadata, and nothing is marked or checked. What PRISM records about what assets are made from is kept either way.</span></span>
        </label>
        {(v.enabled || q.data.enabled) && (
          <div className="rights-fields" data-testid="rights-field-status">
            <p className="section-label" style={{ margin: '4px 0' }}>Rights fields in the metadata model</p>
            <ul className="rights-field-list">
              {status.map((s) => (
                <li key={s.role} data-role={s.role} data-present={Boolean(s.field)}>
                  {s.field ? <Check size={12} color="var(--ready)" /> : <span className="faint">–</span>} {RIGHTS_ROLE_LABELS[s.role]}
                  {s.field && s.field !== RIGHTS_ROLE_LABELS[s.role] && <span className="faint"> · “{s.field}”</span>}
                </li>
              ))}
            </ul>
            {fieldProblems.length > 0 && <div className="banner warning" role="status"><div className="banner-body">{fieldProblems.map((p, i) => <span key={i}>{p}</span>)}</div></div>}
            {missing.length > 0 ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn sm" disabled={adding || !model.data} onClick={() => void addFields()} data-testid="rights-add-fields">
                  {adding ? <Loader2 size={13} className="spin" /> : <Plus size={13} />} Add rights fields
                </button>
                <span className="help">Publishes a new model version with a Rights section{def?.tabs?.length ? ' and tab' : ''} and the {missing.length} missing field{missing.length === 1 ? '' : 's'}. You can relabel, reorder or hide them in the model like any field.</span>
              </div>
            ) : <span className="help" data-testid="rights-fields-ready">Every rights field is in the model. Change them in <Link to="/admin/model">Metadata model</Link>.</span>}
            <span className="help" style={{ display: 'block' }} data-testid="rights-used-for-hint">A field you already have can take a rights role: set its “Used for” in <Link to="/admin/model">Metadata model</Link>.</span>
          </div>
        )}
      </section>

      <section className="panel-card" data-testid="rights-policy">
        <p className="section-label">When material is used</p>
        <p className="muted" style={{ margin: 0 }}>What happens on an export, a Send to edit, a share link or a workflow delivery, for each rights state. Ending soon shows on the mark only.</p>
        <TableScroll>
          <table className="table compact">
            <thead><tr><th>Rights state</th><th>What happens</th></tr></thead>
            <tbody>
              {policyRows(v.policy).map((r) => (
                <tr key={r.state} data-state={r.state}>
                  <td>{r.label}<br /><span className="faint" style={{ fontSize: 12 }} data-testid={`rights-policy-cause-${r.state}`}>{rightsStateCause(r.state, def)}</span></td>
                  <td>
                    <select className="select" value={r.value} disabled={r.fixed} aria-label={`When ${r.label.toLowerCase()}`} data-testid={`rights-policy-${r.state}`}
                      title={r.fixed ? 'A withdrawn asset (a kill order) is always blocked' : undefined}
                      onChange={(e) => setV({ ...v, policy: setPolicy(v.policy, r.state, e.target.value as typeof r.value) })}>
                      {r.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <span className="help">Block: it can’t be done. Block, rights managers can override: someone with Manage rights goes ahead with a reason, which is kept. Warn: anyone goes ahead after ticking “I’ve read the rights restrictions”. Allow: no question; the mark still shows.</span>
      </section>

      <section className="panel-card" data-testid="rights-timing">
        <p className="section-label">Dates and the daily email</p>
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div className="field"><label htmlFor="rights-ending-days">Ending soon</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input id="rights-ending-days" className="input" type="number" min={1} max={365} style={{ maxWidth: 90 }} value={v.endingSoonDays}
                onChange={(e) => setV({ ...v, endingSoonDays: Math.max(1, Math.min(365, Math.round(Number(e.target.value) || 1))) })} data-testid="rights-ending-days" />
              <span className="muted">days before Available until</span>
            </div>
          </div>
          <div className="field">
            <label className="toggle-row" style={{ marginBottom: 4 }}>
              <input type="checkbox" checked={v.digestEnabled} onChange={(e) => setV({ ...v, digestEnabled: e.target.checked })} data-testid="rights-digest-enabled" />
              <span>Send the daily email</span>
            </label>
            <label htmlFor="rights-digest-hour">Daily email at</label>
            <select id="rights-digest-hour" className="select" value={v.digestHour} disabled={!v.digestEnabled} onChange={(e) => setV({ ...v, digestHour: Number(e.target.value) })} data-testid="rights-digest-hour">
              {HOURS.map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
            </select>
            <span className="help" data-testid="rights-digest-help">{v.digestEnabled
              ? 'To people who manage rights: what ends soon, what ended, and finished assets a source turned red. Nothing is sent when there is nothing to say.'
              : 'Off: no daily email is sent. Rights are still marked and checked.'}</span>
          </div>
          <div className="field"><label htmlFor="rights-time-zone">Time zone</label>
            <select id="rights-time-zone" className="select" value={v.timeZone} onChange={(e) => setV({ ...v, timeZone: e.target.value })} data-testid="rights-time-zone">
              {timeZones(v.timeZone).map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
            <span className="help">Available until ends at the end of that day here; watch folder and live source dates are counted here.</span>
          </div>
        </div>
      </section>

      <div className="form-footer">
        <button className="btn primary" disabled={!dirty || save.isPending} onClick={() => void submit()} data-testid="rights-save">
          {save.isPending ? <Loader2 size={14} className="spin" /> : <Check size={14} />} Save
        </button>
        <button className="btn ghost" disabled={!dirty} onClick={() => setV(saved)}>Discard</button>
        {q.data.updatedAt && <span className="faint" style={{ fontSize: 12 }}>Saved {new Date(q.data.updatedAt).toLocaleString()}</span>}
      </div>

      {q.data.enabled && (
        <section className="panel-card" data-testid="rights-counts">
          <p className="section-label">In the library now</p>
          <div className="rights-counts">
            {RIGHTS_COUNT_KEYS.map((k) => (
              <Link key={k} to={rightsFilterHref(k)} className="rights-count" data-testid={`rights-count-${k}`} data-key={k}>
                <strong>{counts.data?.[k] ?? '–'}</strong>
                <span>{rightsCountLabel(k, q.data!.endingSoonDays)}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
